import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { useIntl } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import SceneBackground from './SceneBackground'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 16,
        padding: '12px 20px 20px',
        overflow: 'auto',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 10,
            padding: '8px 16px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '4px 10px 12px',
            gap: 10,
        },
    },
    sceneContainer: {
        width: '100%',
        maxWidth: 520,
        flexShrink: 0,
        minHeight: 100,
        borderRadius: 16,
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: 'min(520px, 45vh)',
        },
        '@media (max-width: 599px)': {
            borderRadius: 10,
        },
    },
    questionText: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
        maxWidth: 480,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 17,
        },
        '@media (max-width: 599px)': {
            fontSize: 17,
            maxWidth: '100%',
        },
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        maxWidth: 480,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 8,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            gap: 8,
        },
    },
    optionButton: {
        width: '100%',
        padding: '16px 20px',
        borderRadius: 16,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 14,
            padding: '12px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 14px',
            fontSize: 14,
            borderRadius: 12,
        },
    },
    optionCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
            borderColor: CapeeshColors.GreenBright,
        },
    },
    optionIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(255, 102, 110, 0.25)',
            borderColor: CapeeshColors.PinkBright,
        },
    },
    optionDisabled: {
        opacity: 0.5,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    optionExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderColor: 'rgba(255, 255, 255, 0.45)',
        },
    },
    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        textAlign: 'center',
        maxWidth: 480,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 12,
        },
    },
    feedbackCorrect: {
        color: CapeeshColors.GreenBright,
    },
    feedbackIncorrect: {
        color: CapeeshColors.PinkBright,
    },
}))

const BranchingMultiChoiceNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isExamMode, isNativeSpeaker = false, l2LanguageId }) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const [selectedIndex, setSelectedIndex] = useState(null)
    const [showFeedback, setShowFeedback] = useState(false)

    const animationDuration = node.scenarioMentors?.[0]?.animationLengthInSeconds || 1.5

    const questionText = (node.texts || []).find((t) => t.textType === 5)?.text
        || (node.texts || []).find((t) => t.textType === 1)?.text
        || ''

    const options = node.options || []

    const shuffledOrder = useMemo(() => {
        const indices = options.map((_, i) => i)
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[indices[i], indices[j]] = [indices[j], indices[i]]
        }
        return indices
    }, [options.length])

    useEffect(() => {
        const questionAudioUrl = (node.texts || []).find((t) => t.textAudioUrl)?.textAudioUrl
        const blobUrl = questionAudioUrl ? audioMap[questionAudioUrl] : null
        if (blobUrl) {
            audioPlayer.playAudio(blobUrl)
        }
        return () => audioPlayer.stopAudio()
    }, [])

    const handleSelect = useCallback(
        async (originalIndex) => {
            if (selectedIndex !== null) return
            setSelectedIndex(originalIndex)
            setShowFeedback(true)

            const opt = options[originalIndex]
            const optAudioUrl = opt?.textAudioUrl ? audioMap[opt.textAudioUrl] : null

            const minDelay = new Promise((r) => setTimeout(r, 1500))

            if (optAudioUrl) {
                await Promise.all([audioPlayer.playAudio(optAudioUrl), minDelay])
            } else {
                await minDelay
            }

            onNodeComplete({
                nodeId: node.nodeId,
                chosenAlternativeIndex: originalIndex,
            })
        },
        [selectedIndex, options, audioMap, audioPlayer, node.nodeId, onNodeComplete]
    )

    const isCorrectOption = (opt) => opt.scoreAlteration >= 0 && !opt.failure
    const selectedOption = selectedIndex !== null ? options[selectedIndex] : null
    const isSelectedCorrect = selectedOption ? isCorrectOption(selectedOption) : null

    return (
        <div className={classes.container}>
            <div className={classes.sceneContainer}>
                <SceneBackground
                    node={node}
                    previousNode={previousNode}
                    imageMap={imageMap}
                    duration={animationDuration}
                />
            </div>

            <Typography className={classes.questionText}>
                {renderFormattedText(questionText)}
            </Typography>

            <div className={classes.optionsContainer}>
                {shuffledOrder.map((origIdx) => {
                    const opt = options[origIdx]
                    if (!opt) return null

                    let extraClass = ''
                    if (showFeedback) {
                        if (isExamMode) {
                            if (origIdx === selectedIndex) {
                                extraClass = classes.optionExamSelected
                            }
                        } else {
                            if (origIdx === selectedIndex) {
                                extraClass = isCorrectOption(opt)
                                    ? classes.optionCorrect
                                    : classes.optionIncorrect
                            } else {
                                extraClass = classes.optionDisabled
                            }
                        }
                    }

                    return (
                        <button
                            key={origIdx}
                            className={`${classes.optionButton} ${extraClass}`}
                            onClick={() => handleSelect(origIdx)}
                            disabled={selectedIndex !== null}
                        >
                            {opt.text || intl.formatMessage(playerMessages.optionNumber, { number: origIdx + 1 })}
                        </button>
                    )
                })}
            </div>

            {showFeedback && !isExamMode && (
                <Typography
                    className={`${classes.feedbackText} ${
                        isSelectedCorrect ? classes.feedbackCorrect : classes.feedbackIncorrect
                    }`}
                >
                    {isSelectedCorrect ? intl.formatMessage(playerMessages.correctFeedback) : intl.formatMessage(playerMessages.incorrectFeedback)}
                </Typography>
            )}
        </div>
    )
}

export default BranchingMultiChoiceNode
